import React, { Fragment, useEffect } from "react";
import { useFormikContext } from "formik";
import { getFieldPath, percentTransformer } from "utils/form";
import get from "lodash/get";
import CurrencyInput from "components/FormerEditor/CurrencyInput";
import SectionBezel from "components/SectionBezel";
import SegmentedControl from "components/SegmentedControl";
import {
  useCountrySelections,
  useCtxReferences,
  useStateSelections
} from "utils/selectors";
import Label from "components/Form/fields/Label";
import {
  TAX_RATE_CLUSIVITY,
  TAX_RATE_CLUSIVITY_OPTIONS,
  TAX_RATE_FORM_KEY
} from "utils/constants/taxRate";
import { FORM_KEY } from "utils/constants/form";
import MultiSelect from "components/Form/fields/MultiSelect";
import { getStripeDestination } from "utils/stripe";
import { COUNTRY_US } from "components/FormerEditor/common/constants/currency";
import { getAccountConnectStatusProps } from "utils/merchantAccount";
import { getIconForUID } from "components/FormerEditor/utils";
import StripeExternalLink from "components/StripeExternalLink";

/**
 * Dynamic tax rates is a beta feature and not yet out of preview
 */
const DYNAMIC_TAX_RATES_ENABLED = false;
/**
 * Set
 * - tax rate percentage
 * - clusivity
 * - country
 * - state
 * @param {String} name
 */
const RateFields = ({ name }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const { merchantAccount } = useCtxReferences();
  const account =
    merchantAccount && getAccountConnectStatusProps(merchantAccount);

  const percentagePath = getFieldPath(name, "percentage");
  const countryPath = getFieldPath(name, "country");
  const statePath = getFieldPath(name, "state");
  const inclusivePath = getFieldPath(name, "inclusive");

  const clusivityTypePath = getFieldPath(
    name,
    `${FORM_KEY}.${TAX_RATE_FORM_KEY.CLUSIVITY}`
  );

  const idPath = getFieldPath(name, "id");
  const id = get(values, idPath);
  const editMode = Boolean(id);

  const percentage = get(values, percentagePath);
  const country = get(values, countryPath);
  const state = get(values, statePath);
  const inclusive = get(values, inclusivePath);
  const clusivity = get(values, clusivityTypePath);

  const percentTouched = get(touched, percentagePath);
  const countryTouched = get(touched, countryPath);
  const stateTouched = get(touched, statePath);
  const clusivityTouched = get(touched, clusivityTypePath);

  const {
    options: countryOptions,
    selections: countrySelections
  } = useCountrySelections(country ? [country] : null);
  const {
    options: stateOptions,
    selections: stateSelections
  } = useStateSelections(state ? [state] : null);

  const inputProps = {
    placeholder: `0%`,
    type: "text",
    disabled: editMode,
    className: "input--common br-6",
    value: percentage,
    onChange: ({ currentTarget }) => {
      if (!percentTouched) {
        setFieldTouched(percentagePath, true);
      }
      setFieldValue(percentagePath, percentTransformer(currentTarget.value));
    },
    maskOptions: {
      prefix: "",
      suffix: "%",
      integerLimit: 3
    }
  };

  const countrySelectProps = {
    value: countrySelections,
    placeholder: "Select the country for this tax rate",
    options: countryOptions,
    isMulti: false,
    disabled: editMode,
    onChange: () => ({ value }) => {
      setFieldValue(countryPath, value);
      if (!countryTouched) {
        setFieldTouched(countryPath, true);
      }
    },
    labelTransform: (value) => value
  };

  const stateSelectProps = {
    value: stateSelections,
    placeholder: "Select the state for this tax rate",
    options: stateOptions,
    isMulti: false,
    disabled: editMode,
    onChange: () => ({ value }) => {
      setFieldValue(statePath, value);
      if (!stateTouched) {
        setFieldTouched(statePath, true);
      }
    },
    labelTransform: (value) => value
  };

  const segmentProps = {
    options: TAX_RATE_CLUSIVITY_OPTIONS,
    disabled: editMode,
    theme: "slim",
    value: clusivity,
    onClick: (_, selection) => {
      if (!clusivityTouched) {
        setFieldTouched(clusivityTypePath, true);
      }
      setFieldValue(clusivityTypePath, selection);
    }
  };

  /**
   * Set inclusive based on clusivity type
   */
  useEffect(() => {
    const clusivityState = clusivity === TAX_RATE_CLUSIVITY.INCLUSIVE;

    if (inclusive !== clusivityState) {
      setFieldValue(inclusivePath, clusivityState);
    }
  }, [clusivity]);

  /**
   * State only optionally supported for US
   * If the country changes from being the US we need to unset the state value
   * */
  const isUS = country === COUNTRY_US;
  useEffect(() => {
    if (!isUS) {
      setFieldValue(statePath, "");
    }
  }, [isUS]);

  const LockIcon = getIconForUID("lock");

  const actionNode = editMode ? (
    <div className="w-100 flex flex-row items-center justify-center">
      <div className="pr2 flex items-center justify-center">
        <LockIcon size={18} />
      </div>
      <StripeExternalLink
        account={account}
        copy={` `}
        tooltip="Manage on Stripe"
        href={getStripeDestination({ path: `tax-rates/${id}` })}
      />
    </div>
  ) : null;

  return (
    <SectionBezel header="Rate" action={actionNode}>
      <div className="w-100 flex flex-column justify-between items-center">
        <div className="f5 w-100 fw5">{`Percentage`}</div>
        <div className="f5 w-100 pt3">
          <CurrencyInput {...inputProps} />
        </div>
      </div>
      <div className="w-100 pt3">
        <SegmentedControl {...segmentProps} />
      </div>
      {DYNAMIC_TAX_RATES_ENABLED && (
        <Fragment>
          <div className="pt3">
            <Label label="Country" />
            <MultiSelect {...countrySelectProps} />
          </div>
          {isUS && (
            <div className="pt3">
              <Label label="State" />
              <MultiSelect {...stateSelectProps} />
            </div>
          )}
        </Fragment>
      )}
    </SectionBezel>
  );
};

export default RateFields;
